import { parseCategories } from '@/utils/paramsUtils';
import { numberFormat, unitToString } from '@/utils/unitUtils';
import PropTypes from 'prop-types';
import { Fragment } from 'react';

const findColumn = (data, parent, sub) => data?.find(col => col.category?.[0] === parent && col.category?.[1] === sub);

const formatValue = item => {
  if (item === 0) return '—';
  if (Number.isInteger(item) && item <= Number.MAX_SAFE_INTEGER) return item;
  return numberFormat(item, 2);
};

const CashFlowDataGrid = ({ data }) => {
  if (!data || data.length === 0) return null;

  const categories = parseCategories(data);
  const yearCol = findColumn(data, 'Indicators', 'Year');
  const opYearCol = findColumn(data, 'Indicators', 'Operation Year');

  const years = yearCol?.data ?? [];
  const operationYears = opYearCol?.data ?? [];

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
            <th className="sticky left-0 z-10 bg-white py-2 pr-3 text-left font-normal min-w-[220px]">Line item</th>
            {years.map((year, idx) => {
              const opYear = operationYears?.[idx];
              const isConstruction = opYear != null && opYear < 0;
              return (
                <th
                  key={`${year}-${idx}`}
                  className={`px-2 py-2 text-right font-normal whitespace-nowrap ${isConstruction ? 'text-gray-400' : 'text-gray-500'}`}
                  title={isConstruction ? 'Construction year' : undefined}
                >
                  {year}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Object.values(categories).map(({ categoryName, params }) => {
            const subCategories = parseCategories(params, 1);
            const rows = Object.values(subCategories);

            return (
              <Fragment key={categoryName}>
                <tr>
                  <td className="sticky left-0 z-10 bg-white pt-4 pb-1 pr-3 text-[11px] uppercase tracking-wide text-gray-500 font-semibold whitespace-nowrap">
                    {categoryName}
                  </td>
                  <td colSpan={years.length} className="pt-4 pb-1" />
                </tr>
                {rows.map(({ categoryName: rowLabel, params: rowParams }) => {
                  const { data: values, unit, total } = rowParams[0];
                  // Skip the Year row inside Indicators — already shown in the table header
                  if (categoryName === 'Indicators' && rowLabel === 'Year') return null;
                  return (
                    <tr
                      key={`${categoryName}-${rowLabel}`}
                      className={`border-b border-gray-50 ${total ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                    >
                      <td className="sticky left-0 z-10 bg-white py-1.5 pr-3 text-left">
                        <div className="flex justify-between gap-3">
                          <span>{rowLabel}</span>
                          <span className="text-gray-500 font-normal text-[11px]">{unitToString(unit)}</span>
                        </div>
                      </td>
                      {values.map((item, idx) => (
                        <td
                          key={`${categoryName}-${rowLabel}-${idx}`}
                          className={`px-2 py-1.5 text-right tabular-nums ${item < 0 ? 'text-rose-600' : ''}`}
                        >
                          {formatValue(item)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

CashFlowDataGrid.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
};

export default CashFlowDataGrid;
